import { Link } from 'react-router-dom';
import { ArrowRight, Globe, MessageSquare, Calendar, Settings, Filter, FolderOpen } from 'lucide-react';
import { useTyped } from '../hooks/useTyped';
import { useScrollAnimations } from '../hooks/useScrollAnimation';
import SEO from '../components/SEO';

const SOLUTIONS = [
  {
    icon: Globe,
    title: 'Professional Websites',
    problem: 'An outdated or unclear website makes potential customers hesitate before reaching out.',
    outcome: 'A clean, modern website that builds trust and makes it easy for visitors to contact you.',
    href: '/solutions/professional-websites',
  },
  {
    icon: MessageSquare,
    title: 'Customer Communication',
    problem: 'Customers who cannot get quick answers often move on silently to other options.',
    outcome: 'Clear, prompt responses that keep inquiries active and customers engaged.',
    href: '/solutions/customer-communication',
  },
  {
    icon: Calendar,
    title: 'Appointment Coordination',
    problem: 'Scheduling back-and-forth creates friction and wastes time on both sides.',
    outcome: 'Confirmations, reminders, and schedules handled reliably without the back-and-forth.',
    href: '/solutions/appointment-coordination',
  },
  {
    icon: Settings,
    title: 'Business Workflow Support',
    problem: 'Repetitive administrative work pulls attention away from what actually matters.',
    outcome: 'Routine operational tasks done consistently without manual follow-up.',
    href: '/solutions/business-workflow',
  },
  {
    icon: Filter,
    title: 'Lead Qualification',
    problem: 'Not every inquiry is a good fit, but answering all of them manually takes time.',
    outcome: 'Important details collected upfront so you can prioritize serious inquiries.',
    href: '/solutions/lead-qualification',
  },
  {
    icon: FolderOpen,
    title: 'Internal Organization',
    problem: 'When teams rely on memory and informal systems, things fall through the gaps.',
    outcome: 'A shared, clear way of tracking tasks so nothing is forgotten.',
    href: '/solutions/internal-organization',
  },
];

const PROCESS_STEPS = [
  {
    number: '01',
    title: 'Understand the Business',
    description:
      "Before anything else, we take time to understand how your business actually operates: where it runs smoothly, where it doesn't, and what your customers experience.",
  },
  {
    number: '02',
    title: 'Identify the Friction',
    description:
      'We look carefully at the gaps between what your business intends to deliver and what customers actually encounter. Small friction points compound over time.',
  },
  {
    number: '03',
    title: 'Build a Smoother Experience',
    description:
      'We design and set up clear processes that address those friction points directly, without unnecessary complexity, and without disrupting what already works.',
  },
  {
    number: '04',
    title: 'Refine Over Time',
    description:
      'Good processes are not set-and-forget. We monitor, adjust, and improve as your business grows and as we learn what your customers need.',
  },
];

const PRINCIPLES = [
  { value: 'Responsiveness', label: 'Every inquiry handled with intention and speed.' },
  { value: 'Consistency', label: 'The same quality experience, every time.' },
  { value: 'Clarity', label: 'Customers always know what to expect.' },
  { value: 'Reliability', label: 'Processes that work even when your team is busy.' },
];

export default function Home() {
  const typed = useTyped();
  const containerRef = useScrollAnimations();

  return (
    <div ref={containerRef}>
      <SEO 
        title="7th Vision | Professional Website Design & Communication" 
        description="We build professional websites and set up clear, reliable communication processes for established businesses."
        schema={JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "7th Vision",
          "description": "Website design and customer communication solutions for established businesses.",
          "url": "https://7thvision.com"
        })}
      />
      {/* Hero */}
      <section className="relative min-h-[85vh] sm:min-h-screen bg-stone-900 flex flex-col justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat bg-fixed opacity-25"
          style={{
            backgroundImage:
              'url(https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=1920)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/60 via-stone-900/80 to-stone-900/95" />

        <div className="relative section-padding pt-28 sm:pt-40 pb-16 sm:pb-32">
          <div className="max-w-4xl">
            <p className="section-label text-bronze-400 mb-8 fade-in">
              Website Design &amp; Customer Communication
            </p>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-stone-100 leading-tight mb-6 sm:mb-10 min-h-[4em] sm:min-h-[3.5em] md:min-h-[2.8em]">
              <span className="typed-cursor">{typed}</span>
            </h1>
            <p className="text-stone-400 text-base sm:text-lg font-light leading-relaxed max-w-xl mb-8 sm:mb-14 fade-up">
              We build professional websites and set up clear, reliable communication processes for established businesses, so you never miss a lead or lose a customer to slow response times.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 fade-up">
              <Link to="/contact" className="btn-outline-light text-center">
                Schedule a Conversation
              </Link>
              <a href="#solutions" className="btn-outline-light text-center">
                View Solutions
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-stone-500 animate-bounce">
          <div className="w-px h-10 bg-stone-600" />
        </div>
      </section>

      {/* Operational Principles */}
      <section className="bg-stone-100 section-padding py-10 md:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
            {PRINCIPLES.map((p, i) => (
              <div
                key={p.value}
                className="fade-up"
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="w-8 h-px bg-bronze-400 mb-4" />
                <p className="font-serif text-lg text-stone-800 mb-2">{p.value}</p>
                <p className="text-xs text-stone-500 leading-relaxed">{p.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="section-padding py-14 md:py-32 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10 md:mb-20 fade-up">
            <p className="section-label mb-4">What We Do</p>
            <h2 className="font-serif text-4xl md:text-5xl text-stone-800 max-w-2xl leading-tight">
              What communication or website challenge is your business facing today?
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-stone-200">
            {SOLUTIONS.map((s, i) => (
              <Link
                key={s.href}
                to={s.href}
                className="group bg-stone-50 p-10 flex flex-col hover:bg-stone-900 transition-colors duration-500 fade-up"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <s.icon
                  size={24}
                  className="text-bronze-500 mb-6 group-hover:text-bronze-400 transition-colors duration-500"
                />
                <h3 className="font-serif text-xl text-stone-800 group-hover:text-stone-100 mb-3 transition-colors duration-500">
                  {s.title}
                </h3>
                <p className="text-sm text-stone-500 group-hover:text-stone-400 leading-relaxed mb-6 flex-1 transition-colors duration-500">
                  {s.problem}
                </p>
                <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-bronze-600 group-hover:text-bronze-400 transition-colors duration-500">
                  <span>Learn More</span>
                  <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform duration-300" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="section-padding py-14 md:py-32 bg-stone-800">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-24 items-start">
            <div className="fade-up">
              <p className="section-label text-bronze-400 mb-4">Our Approach</p>
              <h2 className="font-serif text-4xl md:text-5xl text-stone-100 leading-tight mb-8">
                Thoughtful by design. Reliable by practice.
              </h2>
              <div className="w-12 h-px bg-bronze-500 mb-8" />
              <p className="text-stone-400 leading-relaxed text-lg font-light">
                Every business has its own rhythm, its own gaps, and its own customers to serve.
                We take the time to understand yours before recommending anything.
              </p>
            </div>

            <div className="space-y-8 md:space-y-12">
              {PROCESS_STEPS.map((step, i) => (
                <div
                  key={step.number}
                  className="flex gap-8 fade-up"
                  style={{ transitionDelay: `${i * 120}ms` }}
                >
                  <div className="flex-shrink-0">
                    <span className="font-serif text-4xl text-stone-700 leading-none">
                      {step.number}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-stone-200 mb-2">{step.title}</h3>
                    <p className="text-stone-500 text-sm leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Operational Scenarios */}
      <section className="section-padding py-14 md:py-32 bg-stone-50">
        <div className="max-w-6xl mx-auto">
          <div className="mb-10 md:mb-20 fade-up">
            <p className="section-label mb-4">Common Situations</p>
            <h2 className="font-serif text-4xl md:text-5xl text-stone-800 max-w-2xl leading-tight">
              Operational gaps that are easy to overlook, until they compound.
            </h2>
          </div>

          <div className="space-y-1">
            {[
              {
                before: 'A business misses after-hours inquiries because no one is available to respond.',
                after: 'Inquiries are captured, acknowledged, and organized before the next working day begins.',
              },
              {
                before: 'A team answers the same customer questions repeatedly, every single day.',
                after: 'Common questions are handled consistently and instantly, without staff involvement.',
              },
              {
                before: 'Potential customers cannot easily reach the business, so they choose someone else.',
                after: 'Clear, accessible communication channels reduce friction and keep conversations open.',
              },
              {
                before: 'A website looks outdated and creates hesitation before a conversation even starts.',
                after: 'A professional digital presence builds confidence and makes the first impression count.',
              },
              {
                before: 'Hours each week are spent on repetitive administrative coordination.',
                after: 'Routine processes run reliably without requiring constant manual attention.',
              },
            ].map((scenario, i) => (
              <div
                key={i}
                className="grid grid-cols-1 md:grid-cols-2 gap-px bg-stone-200 fade-up"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="bg-stone-100 p-8">
                  <p className="text-xs tracking-widest uppercase text-stone-400 mb-4">Before</p>
                  <p className="text-stone-600 leading-relaxed">{scenario.before}</p>
                </div>
                <div className="bg-white p-8 border-l-2 border-bronze-400">
                  <p className="text-xs tracking-widest uppercase text-bronze-600 mb-4">After</p>
                  <p className="text-stone-700 leading-relaxed">{scenario.after}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Digital Presence */}
      <section className="section-padding py-14 md:py-32 bg-stone-900 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-20 items-center">
            <div className="fade-up">
              <p className="section-label text-bronze-400 mb-4">Your Digital Front Desk</p>
              <h2 className="font-serif text-4xl md:text-5xl text-stone-100 leading-tight mb-8">
                Your website is often the first impression a customer has of your business.
              </h2>
              <div className="w-12 h-px bg-bronze-500 mb-8" />
              <p className="text-stone-400 leading-relaxed mb-6 font-light">
                A well-built website does more than look professional. It helps visitors
                understand what you do, find what they need, and take the next step without
                confusion or hesitation.
              </p>
              <p className="text-stone-400 leading-relaxed mb-10 font-light">
                It should work well on every device, load quickly, and feel as organized and
                trustworthy as the business behind it.
              </p>
              <Link to="/solutions/professional-websites" className="btn-outline-light">
                About Our Websites
              </Link>
            </div>

            <div className="relative fade-in">
              <div className="bg-stone-800 rounded-sm overflow-hidden shadow-2xl">
                {/* Browser chrome */}
                <div className="bg-stone-700 px-4 py-3 flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <div className="w-3 h-3 rounded-full bg-stone-500" />
                    <div className="w-3 h-3 rounded-full bg-stone-500" />
                    <div className="w-3 h-3 rounded-full bg-stone-500" />
                  </div>
                  <div className="flex-1 bg-stone-600 rounded-sm h-5 mx-2" />
                </div>
                {/* Mock website content */}
                <div className="p-6 space-y-4">
                  <div className="h-32 bg-stone-700 rounded-sm flex items-center justify-center">
                    <img
                      src="https://images.pexels.com/photos/1170412/pexels-photo-1170412.jpeg?auto=compress&cs=tinysrgb&w=600"
                      alt="Professional office"
                      className="w-full h-full object-cover opacity-60 rounded-sm"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-stone-600 rounded-sm w-3/4" />
                    <div className="h-3 bg-stone-700 rounded-sm w-full" />
                    <div className="h-3 bg-stone-700 rounded-sm w-5/6" />
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[1, 2, 3].map((n) => (
                      <div key={n} className="h-12 bg-stone-700 rounded-sm" />
                    ))}
                  </div>
                  <div className="h-8 bg-bronze-700/40 rounded-sm w-1/3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding py-14 md:py-28 bg-stone-100">
        <div className="max-w-3xl mx-auto text-center fade-up">
          <p className="section-label mb-6">Ready to Begin?</p>
          <h2 className="font-serif text-4xl md:text-5xl text-stone-800 leading-tight mb-8">
            A single conversation is often enough to identify where things can improve.
          </h2>
          <div className="w-12 h-px bg-bronze-400 mx-auto mb-10" />
          <p className="text-stone-500 leading-relaxed mb-12 text-lg font-light">
            We do not offer generic packages or one-size solutions. Every engagement starts
            with understanding what your business actually needs.
          </p>
          <Link to="/contact" className="btn-primary">
            Schedule a Conversation
          </Link>
        </div>
      </section>
    </div>
  );
}
